import { create } from 'zustand';
import { devtools, persist, createJSONStorage } from 'zustand/middleware';
import { DEFAULT_CURRENCY, getCurrencyInfo } from '@/config/currencies';

/**
 * Booking wizard store
 *
 * trip            – from FlightSearchForm (includes optional originMeta / destinationMeta)
 * bookingId       — from API step 1 (passengers); needed for API step 2 (checkout).
 * bookingApiError – last API error message
 */

const initialState = {
  step: 1,
  currency: DEFAULT_CURRENCY,
  trip: null,
  passengers: [],
  email: '',
  bookingId: null,
  persistedAt: null,
  bookingApiSubmitting: false,
  bookingApiError: null,
  /** Step 1 form passes validation (trip search). */
  tripFormReady: false,
  /** Step 2 form passes validation (contact + travellers). */
  passengerFormReady: false,
  couponCode: '',
  couponDiscount: 0, // percentage e.g. 10
  internalNote: '',
  isChecked: false,
  paypalClientId: null,
  activeGateway: 'stripe_checkout',
  timeline: [
    {
      status: 'FlightSearchStarted',
      message: 'Flight Search Started',
      at: new Date().toISOString(),
    },
  ],
};

const BOOKING_DRAFT_TTL_MS = 24 * 60 * 60 * 1000; // 1 day

function hasReviewDraft(state) {
  return !!(
    state?.trip &&
    Array.isArray(state?.passengers) &&
    state.passengers.length > 0 &&
    state?.email &&
    String(state.email).trim() &&
    state?.bookingId
  );
}

const useBookingStore = create(
  devtools(
    persist(
      (set, get) => ({
        ...initialState,

        addTimelineEvent(status, message, at = new Date().toISOString()) {
          if (!status || !message) return;
          const current = Array.isArray(get().timeline) ? [...get().timeline] : [];
          const isRetry = message === 'Flight Search (Retry)' || message.toLowerCase().includes('retry');
          const exists = current.some((e) => e.status === status);
          if (!isRetry && exists) {
            return;
          }
          set({ timeline: [...current, { status, message, at }] }, false, 'addTimelineEvent');
        },

        resetTimeline(initialEvents = []) {
          const defaultInit = [
            {
              status: 'FlightSearchStarted',
              message: 'Flight Search Started',
              at: new Date().toISOString(),
            },
          ];
          set({ timeline: initialEvents.length > 0 ? initialEvents : defaultInit }, false, 'resetTimeline');
        },

        setCurrency(code) {
          const info = getCurrencyInfo(code);
          set({ currency: info.code }, false, 'setCurrency');
        },

        setBookingSubmitting(v) {
          set({ bookingApiSubmitting: !!v }, false, 'setBookingSubmitting');
        },

        setBookingApiError(msg) {
          set({ bookingApiError: msg ? String(msg) : null }, false, 'setBookingApiError');
        },

        clearBookingApiError() {
          set({ bookingApiError: null }, false, 'clearBookingApiError');
        },

        setTripFormReady(v) {
          set({ tripFormReady: !!v }, false, 'setTripFormReady');
        },

        setPassengerFormReady(v) {
          set({ passengerFormReady: !!v }, false, 'setPassengerFormReady');
        },

        setIsChecked(v) {
          set({ isChecked: !!v }, false, 'setIsChecked');
        },

        applyCoupon(code) {
          const formattedCode = String(code || '').trim().toUpperCase();
          let discount = 0;
          if (formattedCode === 'WELCOME10') {
            discount = 10;
          } else if (formattedCode === 'TRAVEL20') {
            discount = 20;
          } else if (formattedCode === 'PROMO15') {
            discount = 15;
          } else if (formattedCode === 'SUPER50') {
            discount = 50;
          }

          if (discount > 0) {
            set({ couponCode: formattedCode, couponDiscount: discount }, false, 'applyCoupon/success');
            return true;
          } else {
            set({ couponCode: '', couponDiscount: 0 }, false, 'applyCoupon/failed');
            return false;
          }
        },

        removeCoupon() {
          set({ couponCode: '', couponDiscount: 0 }, false, 'removeCoupon');
        },

        /** Trip-only UI step — no booking API until passenger step. */
        confirmTrip(trip) {
          set(
            {
              trip,
              persistedAt: Date.now(),
              bookingApiError: null,
              step: 2,
            },
            false,
            'confirmTrip',
          );
          window.scrollTo({ top: 0, behavior: 'smooth' });
        },

        /** Passenger step — API step 1 succeeded; save id for checkout. */
        confirmPassengers({ passengers, email, bookingId: nextId, internalNote, paypalClientId, activeGateway }) {
          set(
            {
              passengers,
              email,
              bookingId: nextId ?? get().bookingId,
              internalNote: internalNote || '',
              paypalClientId: paypalClientId ?? get().paypalClientId,
              activeGateway: activeGateway ?? get().activeGateway,
              persistedAt: Date.now(),
              bookingApiError: null,
              step: 3,
            },
            false,
            'confirmPassengers',
          );
          window.scrollTo({ top: 0, behavior: 'smooth' });
        },

        goBack(target) {
          set(
            {
              step: target === 'trip' ? 1 : target === 'passengers' ? 2 : 3,
              persistedAt: Date.now(),
              isChecked: false,
              ...(target === 'trip'
                ? {
                    bookingApiError: null,
                    tripFormReady: false,
                  }
                : target === 'passengers'
                  ? { passengerFormReady: false }
                  : {}),
            },
            false,
            'goBack',
          );
          window.scrollTo({ top: 0, behavior: 'smooth' });
        },

        reset() {
          set({ ...initialState, isChecked: false }, false, 'reset');
        },
      }),
      {
        name: 'BookingStore',
        storage: createJSONStorage(() => localStorage),
        partialize: (state) => ({
          step: state.step,
          currency: state.currency,
          trip: state.trip,
          passengers: state.passengers,
          email: state.email,
          bookingId: state.bookingId,
          persistedAt: state.persistedAt,
          couponCode: state.couponCode,
          couponDiscount: state.couponDiscount,
          internalNote: state.internalNote,
          paypalClientId: state.paypalClientId,
          timeline: state.timeline,
        }),
        merge: (persistedState, currentState) => {
          const safePersisted =
            persistedState && typeof persistedState === 'object' ? persistedState : {};
          const savedAt =
            typeof safePersisted.persistedAt === 'number' ? safePersisted.persistedAt : 0;
          const isExpired = !savedAt || Date.now() - savedAt > BOOKING_DRAFT_TTL_MS;
          if (isExpired) return { ...currentState };

          const merged = { ...currentState, ...safePersisted };
          return {
            ...merged,
            currency: getCurrencyInfo(merged.currency).code,
            step: merged.step === 4 ? 4 : hasReviewDraft(merged) ? 3 : merged.step,
            bookingApiSubmitting: false,
            bookingApiError: null,
            tripFormReady: false,
            passengerFormReady: false,
            couponCode: merged.couponCode ?? '',
            couponDiscount: merged.couponDiscount ?? 0,
          };
        },
      },
    ),
    { name: 'BookingStoreDevtools' },
  ),
);

export default useBookingStore;
